import { useCallback, useEffect, useRef, useState } from 'react'
import { supabase } from '../lib/supabaseClient'
import { useAuth } from '../contexts/AuthContext'

// Loads reactions + comments for a fixed set of capture ids and keeps
// them live via realtime. One channel covers both tables — the in-app
// pair is two people, so the row volume is trivial and a single
// subscription per pair of capture ids is plenty.
//
// `captureIds` should be a stable array (memoize at the call site or
// rely on referential equality of the array). The hook ignores empty
// inputs to avoid hammering Supabase with degenerate `in.()` queries.
//
// Returned shape:
//   reactionsByCapture: { [captureId]: [{ user_id, emoji, id }] }
//   commentsByCapture:  { [captureId]: [{ id, user_id, body, created_at }] }
//   addReaction(captureId, emoji)
//   removeReaction(captureId, emoji)
//   addComment(captureId, body)
//   loading
export function useMomentInteractions(captureIds) {
  const { user } = useAuth()
  const userId = user?.id || null
  const [reactionsByCapture, setReactions] = useState({})
  const [commentsByCapture, setComments] = useState({})
  const [loading, setLoading] = useState(false)
  const idsRef = useRef([])
  idsRef.current = Array.isArray(captureIds) ? captureIds.filter(Boolean) : []

  const refetch = useCallback(async () => {
    const ids = idsRef.current
    if (!ids.length) {
      setReactions({})
      setComments({})
      return
    }
    const [rxRes, cmRes] = await Promise.all([
      supabase
        .from('moments_reactions')
        .select('id, capture_id, user_id, emoji, created_at')
        .in('capture_id', ids),
      supabase
        .from('moments_comments')
        .select('id, capture_id, user_id, body, created_at')
        .in('capture_id', ids)
        .order('created_at', { ascending: true }),
    ])
    const rxMap = {}
    for (const r of rxRes.data || []) {
      ;(rxMap[r.capture_id] ||= []).push(r)
    }
    const cmMap = {}
    for (const c of cmRes.data || []) {
      ;(cmMap[c.capture_id] ||= []).push(c)
    }
    setReactions(rxMap)
    setComments(cmMap)
  }, [])

  useEffect(() => {
    if (!userId) return undefined
    let alive = true
    setLoading(true)
    refetch().then(() => { if (alive) setLoading(false) })

    const channel = supabase
      .channel(`moments-interactions-${userId}-${Math.random().toString(36).slice(2)}`)
      .on('postgres_changes', { event: '*', schema: 'public', table: 'moments_reactions' },
        () => { if (alive) refetch() })
      .on('postgres_changes', { event: '*', schema: 'public', table: 'moments_comments' },
        () => { if (alive) refetch() })
      .subscribe()
    return () => { alive = false; supabase.removeChannel(channel) }
  }, [userId, refetch, captureIds])

  const addReaction = useCallback(async (captureId, emoji) => {
    if (!userId || !captureId || !emoji) return
    // Toggle: if I already have this emoji on this capture, remove it.
    const existing = (reactionsByCapture[captureId] || []).find(
      (r) => r.user_id === userId && r.emoji === emoji,
    )
    if (existing) {
      await supabase.from('moments_reactions').delete().eq('id', existing.id)
    } else {
      await supabase.from('moments_reactions').insert({
        capture_id: captureId,
        user_id: userId,
        emoji,
      })
    }
    await refetch()
  }, [userId, reactionsByCapture, refetch])

  const addComment = useCallback(async (captureId, body) => {
    if (!userId || !captureId) return
    const text = (body || '').trim()
    if (!text) return
    await supabase.from('moments_comments').insert({
      capture_id: captureId,
      user_id: userId,
      body: text,
    })
    await refetch()
  }, [userId, refetch])

  return { reactionsByCapture, commentsByCapture, addReaction, addComment, loading }
}
